import { z } from 'zod';

import localStorageStore from '../../store/localStorageStore';

export interface SidebarItem {
  readonly name: string;
  readonly link?: string;
  readonly children?: readonly SidebarItem[];
}

export const sidebarItemSchema: z.ZodType<SidebarItem> = z
  .object({
    name: z.string(),
    link: z.string().optional(),
    children: z.lazy(() => z.array(sidebarItemSchema)).optional(),
  })
  .passthrough();

export const sidebarSchema = z.array(sidebarItemSchema);

export const SIDEBAR_KEY = 'sideBar';

export const SIDEBAR_STORED_AT_KEY = 'sideBarAt';

export interface StoredSidebar {
  readonly items: readonly SidebarItem[];
  readonly storedAt: number;
}

const parsedJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

export const readStoredSidebarAt = (): number => Number(localStorageStore.getItem(SIDEBAR_STORED_AT_KEY) ?? 0);

export const readStoredSidebar = (): StoredSidebar | null => {
  const raw = localStorageStore.getItem(SIDEBAR_KEY);
  if (raw === null) return null;
  const parsed = sidebarSchema.safeParse(parsedJson(raw));
  if (!parsed.success) return null;
  return { items: parsed.data, storedAt: readStoredSidebarAt() };
};

export const clearStoredSidebar = (): void => {
  localStorageStore.removeItem(SIDEBAR_KEY);
  localStorageStore.removeItem(SIDEBAR_STORED_AT_KEY);
};

export const storeSidebar = (items: readonly SidebarItem[], storedAt: number): void => {
  localStorageStore.setItem(SIDEBAR_KEY, JSON.stringify(items));
  localStorageStore.setItem(SIDEBAR_STORED_AT_KEY, String(storedAt));
};

export const visibleItems = (items: readonly SidebarItem[], canVisit: (to: string) => boolean): SidebarItem[] =>
  items.flatMap((item): SidebarItem[] => {
    const children = item.children ? visibleItems(item.children, canVisit) : undefined;
    const linkOpens = item.link !== undefined && canVisit(item.link);
    if (item.link !== undefined && !linkOpens) return [];
    if (item.link === undefined && (children === undefined || children.length === 0)) return [];
    return [{ ...item, ...(children ? { children } : {}) }];
  });

export const linksOf = (items: readonly SidebarItem[]): string[] =>
  items.flatMap((item) => [...(item.link !== undefined ? [item.link] : []), ...linksOf(item.children ?? [])]);
